/**
 * Atributos de riesgo de un vehículo y de dónde salió cada uno.
 *
 * Implementa el ADR-004. Cada sistema manda en lo suyo: HighLevel en los
 * atributos de la persona, Sync en la póliza y el vehículo, Alegra como
 * respaldo de ciudad. Aquí solo se guarda el resultado, siempre con su
 * procedencia: un dato de HighLevel y uno de Sync no se confunden nunca.
 *
 * Nada se infiere. Sin dato, el atributo vale `null` y el vehículo cae en la
 * cohorte SIN_DATO, que es visible y se cuenta (constitución, Art. 7).
 */

/** Sistemas de los que puede venir un atributo. */
export type AttributeSystem = "highlevel" | "sync" | "alegra" | "wox" | "manual";

export const COHORT_DIMENSIONS = [
  "aseguradora",
  "marca",
  "linea",
  "modeloAnio",
  "ciudad",
  "departamento",
  "rangoEdad",
  "genero",
  "ocupacion",
  "usoServicio",
  "tipoPersona",
] as const;

export type CohortDimension = (typeof COHORT_DIMENSIONS)[number];

export const DIMENSION_LABELS: Record<CohortDimension, string> = {
  aseguradora: "Aseguradora",
  marca: "Marca",
  linea: "Línea",
  modeloAnio: "Modelo (año)",
  ciudad: "Ciudad de circulación",
  departamento: "Departamento",
  rangoEdad: "Rango de edad",
  genero: "Género",
  ocupacion: "Ocupación",
  usoServicio: "Uso / servicio",
  tipoPersona: "Tipo de titular",
};

/** Un valor con su procedencia. `value: null` significa «no lo sabemos». */
export type AttributeValue = {
  value: string | null;
  system: AttributeSystem | null;
  /** Campo exacto del sistema de origen, para poder rastrearlo. */
  field: string | null;
  /** Cuándo se leyó, ISO 8601. */
  observedAt: string | null;
  /** Otras fuentes que decían algo distinto. No se promedia: se registra. */
  conflicts?: Array<{ system: AttributeSystem; value: string }>;
};

export const UNKNOWN_ATTRIBUTE: AttributeValue = {
  value: null,
  system: null,
  field: null,
  observedAt: null,
};

export type VehicleAttributes = {
  deviceId: string;
  plate: string | null;
  /** Fecha de nacimiento del titular, ISO `YYYY-MM-DD`. De ella sale el rango. */
  birthDate: AttributeValue;
} & Record<Exclude<CohortDimension, "rangoEdad">, AttributeValue>;

/** Etiqueta que reciben los vehículos sin el atributo. */
export const NO_DATA_KEY = "SIN_DATO";

/**
 * Rangos de edad. Son los tramos actuariales de uso común en auto en Colombia.
 * Los bordes están escritos aquí y no repartidos por el código porque cambiar
 * un tramo cambia toda comparación histórica.
 */
export const AGE_BUCKETS: ReadonlyArray<{ label: string; min: number; max: number }> = [
  { label: "18–24", min: 18, max: 24 },
  { label: "25–34", min: 25, max: 34 },
  { label: "35–44", min: 35, max: 44 },
  { label: "45–54", min: 45, max: 54 },
  { label: "55–64", min: 55, max: 64 },
  { label: "65+", min: 65, max: 200 },
];

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/u;

/**
 * Edad cumplida en una fecha dada. Se calcula contra el **fin del período
 * analizado**, no contra hoy: así el mismo informe da el mismo rango cuando se
 * vuelva a abrir dentro de un año (constitución, Art. 1).
 */
export function ageAt(birthDate: string, referenceDate: string): number | null {
  const birth = ISO_DATE.exec(birthDate);
  const reference = ISO_DATE.exec(referenceDate);
  if (!birth || !reference) return null;

  const [, by, bm, bd] = birth.map(Number) as unknown as [string, number, number, number];
  const [, ry, rm, rd] = reference.map(Number) as unknown as [string, number, number, number];
  if (bm < 1 || bm > 12 || bd < 1 || bd > 31) return null;

  let age = ry - by;
  if (rm < bm || (rm === bm && rd < bd)) age--;
  return age >= 0 && age < 200 ? age : null;
}

export function ageBucket(birthDate: string | null, referenceDate: string): string | null {
  if (!birthDate) return null;
  const age = ageAt(birthDate, referenceDate);
  if (age === null) return null;
  return AGE_BUCKETS.find((bucket) => age >= bucket.min && age <= bucket.max)?.label ?? null;
}

/**
 * Valor de una dimensión para un vehículo, ya normalizado a clave de cohorte.
 * `rangoEdad` se deriva de la fecha de nacimiento contra el fin del período.
 */
export function dimensionValue(
  attributes: VehicleAttributes,
  dimension: CohortDimension,
  periodEndDate: string,
): string {
  if (dimension === "rangoEdad") {
    return ageBucket(attributes.birthDate.value, periodEndDate) ?? NO_DATA_KEY;
  }
  const raw = attributes[dimension].value;
  return raw === null || raw.trim() === "" ? NO_DATA_KEY : raw.trim();
}

/** Procedencia de una dimensión, para que la UI pueda mostrar de dónde salió. */
export function dimensionProvenance(
  attributes: VehicleAttributes,
  dimension: CohortDimension,
): AttributeValue {
  return dimension === "rangoEdad" ? attributes.birthDate : attributes[dimension];
}

export function emptyAttributes(deviceId: string, plate: string | null): VehicleAttributes {
  const blank = () => ({ ...UNKNOWN_ATTRIBUTE });
  return {
    deviceId,
    plate,
    birthDate: blank(),
    aseguradora: blank(),
    marca: blank(),
    linea: blank(),
    modeloAnio: blank(),
    ciudad: blank(),
    departamento: blank(),
    genero: blank(),
    ocupacion: blank(),
    usoServicio: blank(),
    tipoPersona: blank(),
  };
}
